import React, { useEffect, useState } from 'react';
import { listFactories } from '../../services/factories';
import { listWorkshops } from '../../services/workshops';
import { listSections } from '../../services/sections';
import { listRooms } from '../../services/rooms';
import { listCameras } from '../../services/cameras';
import { HIERARCHY_LABELS } from '../../utils/hierarchyLabels';
import './CameraHierarchyFilter.css';

const normalize = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

export const EMPTY_HIERARCHY = {
  factoryId: '',
  workshopId: '',
  sectionId: '',
  roomId: '',
  cameraId: '',
};

const LEVELS = [
  { key: 'factoryId', label: HIERARCHY_LABELS.factory.one, placeholder: HIERARCHY_LABELS.factory.all },
  { key: 'workshopId', label: HIERARCHY_LABELS.workshop.one, placeholder: HIERARCHY_LABELS.workshop.all },
  { key: 'sectionId', label: HIERARCHY_LABELS.section.one, placeholder: HIERARCHY_LABELS.section.all },
  { key: 'roomId', label: HIERARCHY_LABELS.room.one, placeholder: HIERARCHY_LABELS.room.all },
  { key: 'cameraId', label: HIERARCHY_LABELS.camera.one, placeholder: HIERARCHY_LABELS.camera.all },
];

/**
 * Опции одного уровня каскада вместе с его состоянием.
 *
 * ТЗ Part 2, 11.1: 403/404 нельзя молча превращать в пустой список, и грузящийся
 * уровень блокирует только свой select, а не всю страницу.
 */
const useCascadeLevel = (parentId, fetchList) => {
  const [level, setLevel] = useState({ options: [], state: 'idle' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!parentId) {
      setLevel({ options: [], state: 'idle' });
      return undefined;
    }
    let cancelled = false;
    setLevel({ options: [], state: 'loading' });
    fetchList(parentId)
      .then((data) => {
        if (cancelled) return;
        const options = normalize(data);
        setLevel({ options, state: options.length ? 'ready' : 'empty' });
      })
      .catch(() => {
        if (cancelled) return;
        setLevel({ options: [], state: 'error' });
      });
    return () => { cancelled = true; };
  }, [parentId, fetchList, reloadToken]);

  return { ...level, reload: () => setReloadToken((token) => token + 1) };
};

/**
 * Каскад Объект → Подразделение → Область → Локация → Камера (ТЗ №1 §3.3;
 * внутренние ключи factory/workshop/section/room не меняются).
 * Контролируемый компонент: выбор хранит родитель, здесь только списки опций.
 */
const CameraHierarchyFilter = ({ value = EMPTY_HIERARCHY, onChange, disabled = false }) => {
  // Объекты (factories) не имеют родителя, поэтому подставляем константу-«родителя»,
  // которую listFactories просто игнорирует.
  const factories = useCascadeLevel('root', listFactories);
  const workshops = useCascadeLevel(value.factoryId, listWorkshops);
  const sections = useCascadeLevel(value.workshopId, listSections);
  const rooms = useCascadeLevel(value.sectionId, listRooms);
  const cameras = useCascadeLevel(value.roomId, listCameras);

  const levelsState = [factories, workshops, sections, rooms, cameras];

  const handleSelect = (levelIndex, rawValue) => {
    const next = { ...value, [LEVELS[levelIndex].key]: rawValue };
    LEVELS.slice(levelIndex + 1).forEach((level) => { next[level.key] = ''; });
    onChange(next);
  };

  const optionLabel = (levelIndex, option) => {
    if (option.name) return option.name;
    return LEVELS[levelIndex].key === 'cameraId' ? `Камера #${option.id}` : `#${option.id}`;
  };

  return (
    <>
      {LEVELS.map((level, index) => {
        const parentKey = index === 0 ? null : LEVELS[index - 1].key;
        const { options, state, reload } = levelsState[index];
        const isDisabled = disabled || (parentKey ? !value[parentKey] : false) || state === 'loading';

        return (
          <div className="chf-field" key={level.key}>
            <label className="chf-field-label" htmlFor={`chf-${level.key}`}>{level.label}</label>
            <select
              id={`chf-${level.key}`}
              className="chf-select"
              value={value[level.key] || ''}
              disabled={isDisabled}
              onChange={(event) => handleSelect(index, event.target.value)}
            >
              <option value="">
                {state === 'loading' ? 'Загружаем…' : level.placeholder}
              </option>
              {options.map((option) => (
                <option key={option.id} value={String(option.id)}>
                  {optionLabel(index, option)}
                </option>
              ))}
            </select>

            {state === 'empty' && <p className="chf-field-note">Нет доступных объектов</p>}
            {state === 'error' && (
              <p className="chf-field-error">
                Не удалось загрузить список.{' '}
                <button type="button" className="chf-retry" onClick={reload}>Повторить</button>
              </p>
            )}
          </div>
        );
      })}
    </>
  );
};

export default CameraHierarchyFilter;
